import { keyframes, css } from 'styled-components';

/**
 * Fade In animation
 */
export const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

/**
 * Fade Out animation
 */
export const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

/**
 * Slide Up animation
 */
export const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Slide Down animation
 */
export const slideDown = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(20px);
  }
`;

/**
 * Scale In animation (for modals)
 */
export const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

/**
 * Pulse animation for badges
 */
export const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
`;

/**
 * Breathing animation for empty states
 */
export const breathe = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.02);
    opacity: 0.8;
  }
`;

/**
 * Checkmark draw animation
 */
export const drawCheckmark = keyframes`
  0% {
    stroke-dashoffset: 100;
  }
  100% {
    stroke-dashoffset: 0;
  }
`;

/**
 * Success circle animation
 */
export const successCircle = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

/**
 * Ripple effect animation
 */
export const ripple = keyframes`
  0% {
    transform: scale(0);
    opacity: 0.5;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
`;

/**
 * Expand height animation
 */
export const expandHeight = keyframes`
  from {
    max-height: 0;
    opacity: 0;
  }
  to {
    max-height: 1000px;
    opacity: 1;
  }
`;

/**
 * Collapse height animation
 */
export const collapseHeight = keyframes`
  from {
    max-height: 1000px;
    opacity: 1;
  }
  to {
    max-height: 0;
    opacity: 0;
  }
`;

/**
 * Utility function to disable animations for reduced motion
 */
export const withReducedMotion = (animation: any) => css`
  animation: ${animation};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

/**
 * Standard transition timing
 */
export const transitions = {
  fast: '150ms ease-in-out',
  normal: '250ms ease-in-out',
  slow: '400ms ease-in-out',
};

/**
 * GPU-accelerated transition properties
 */
export const gpuTransitions = {
  transform: `transform ${transitions.normal}`,
  opacity: `opacity ${transitions.normal}`,
  all: `transform ${transitions.normal}, opacity ${transitions.normal}`,
};

/**
 * Focus visible styles for accessibility
 */
export const focusVisible = css`
  &:focus-visible {
    outline: 3px solid #227B7E;
    outline-offset: 2px;
    border-radius: 4px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

/**
 * Hover lift effect
 */
export const hoverLift = css`
  transition: ${gpuTransitions.all};

  &:hover {
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover {
      transform: none;
    }
  }
`;

/**
 * Card hover effect
 */
export const cardHover = css`
  transition: all ${transitions.normal};
  cursor: pointer;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
    border-color: #227B7E;
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover {
      transform: none;
    }
  }
`;

/**
 * Button press effect
 */
export const buttonPress = css`
  transition: transform ${transitions.fast};
  position: relative;
  overflow: hidden;

  &:active {
    transform: scale(0.98);
  }

  @media (prefers-reduced-motion: reduce) {
    &:active {
      transform: none;
    }
  }
`;

/**
 * Stagger animation delays for list items
 */
export const staggerDelay = (index: number, baseDelay: number = 50) => css`
  animation-delay: ${index * baseDelay}ms;
`;

/**
 * Fade in with stagger
 */
export const fadeInStagger = css`
  animation: ${fadeIn} ${transitions.normal} ease-out backwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`;
